import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * Builds the "word|elementIndex|timestamp" indexed transcript format used by
 * AI Flow prompts, with `[Speaker: Name]` markers inserted at speaker changes.
 *
 * This mirrors the `getChunkWithSpeakerLabelsFormatted` loop used in
 * `produceClipsChunked` (AIClipProducer.ts) but operates over the full
 * element array rather than a per-chunk slice.
 */
export function buildTranscriptWithIndices(
  elements: TranscriptElement[],
  speakerLabelToName: Map<string, string>,
): string {
  let lastSpeaker = '';
  const parts: string[] = [];

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (el.state === 'removed' || el.type !== 'text') continue;
    // Guard against null timestamps (ts can be null at runtime for removed/cut elements)
    if (el.ts == null) continue;

    // Insert speaker marker when speaker changes
    const speakerName = el.speaker_label
      ? (speakerLabelToName.get(el.speaker_label) ?? el.speaker_label)
      : '';
    if (speakerName && speakerName !== lastSpeaker) {
      parts.push(`\n[Speaker: ${speakerName}]`);
      lastSpeaker = speakerName;
    }

    parts.push(`${el.value}|${i}|${el.ts}`);
  }

  return parts.join(' ');
}

/**
 * Builds a plain-text transcript with `[Speaker: Name]` markers.
 * Used for the `storyTranscription` context variable.
 */
export function buildPlainTranscriptWithSpeakers(
  elements: TranscriptElement[],
  speakerLabelToName: Map<string, string>,
): string {
  let lastSpeaker = '';
  const parts: string[] = [];

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (el.state === 'removed' || el.type !== 'text') continue;
    // Guard against null timestamps (ts can be null at runtime for removed/cut elements)
    if (el.ts == null) continue;

    const speakerName = el.speaker_label
      ? (speakerLabelToName.get(el.speaker_label) ?? el.speaker_label)
      : '';
    if (speakerName && speakerName !== lastSpeaker) {
      parts.push(`\n[Speaker: ${speakerName}]`);
      lastSpeaker = speakerName;
    }

    parts.push(`${el.value}`);
  }

  return parts.join(' ');
}
